import React, { useState } from 'react'
import PageHeader from '../components/PageHeader'
import Reveal from '../components/Reveal'
import Icon from '../components/Icons'
import { company } from '../data/site'

const initialForm = { name: '', email: '', phone: '', subject: '', message: '' }

const Contact = () => {
  const [form, setForm] = useState(initialForm)
  const [sent, setSent] = useState(false)

  const update = (e) => setForm({ ...form, [e.target.name]: e.target.value })

  // No backend is wired up yet, so the form hands off to the visitor's mail
  // client. Swap this for a POST to your form endpoint when one exists.
  const handleSubmit = (e) => {
    e.preventDefault()
    const body = [
      `Name: ${form.name}`,
      `Email: ${form.email}`,
      `Phone: ${form.phone}`,
      '',
      form.message,
    ].join('\n')

    window.location.href = `mailto:${company.emails[0]}?subject=${encodeURIComponent(
      form.subject || 'Website enquiry'
    )}&body=${encodeURIComponent(body)}`
    setSent(true)
  }

  return (
    <main>
      <PageHeader
        index="03"
        label="Contact"
        lines={[
          'Let’s scope',
          <>
            your next <span className="accent-italic">project</span>
          </>,
        ]}
        description="Send us the brief, the tender documents, or just a question — we will come back with a clear next step."
        crumb="Contact"
      />

      <section className="section">
        <div className="container">
          <div className="grid gap-16 lg:grid-cols-[0.75fr_1.25fr] lg:gap-24">
            {/* ---------- Details ---------- */}
            <div className="lg:sticky lg:top-32 lg:self-start">
              <Reveal className="flex items-baseline gap-4">
                <span className="index-num">01</span>
                <span className="label">Get in touch</span>
              </Reveal>

              <Reveal delay={80}>
                <p className="mt-8 text-lg leading-relaxed text-ink-500">
                  Reach us directly by phone or email. We respond to enquiries during
                  business hours, Monday to Friday.
                </p>
              </Reveal>

              <dl className="mt-12 divide-y divide-ink-950/10 border-y border-ink-950/10">
                <Reveal delay={140} className="py-6">
                  <dt className="label flex items-center gap-3">
                    <Icon name="phone" className="h-4 w-4 text-accent-500" />
                    Phone
                  </dt>
                  <dd className="mt-3 space-y-1">
                    {company.phones.map((phone) => (
                      <a
                        key={phone}
                        href={`tel:${phone.replace(/\s/g, '')}`}
                        className="block font-display text-lg font-medium text-ink-950 transition-colors hover:text-accent-500"
                      >
                        {phone}
                      </a>
                    ))}
                  </dd>
                </Reveal>

                <Reveal delay={200} className="py-6">
                  <dt className="label flex items-center gap-3">
                    <Icon name="mail" className="h-4 w-4 text-accent-500" />
                    Email
                  </dt>
                  <dd className="mt-3 space-y-1">
                    {company.emails.map((email) => (
                      <a
                        key={email}
                        href={`mailto:${email}`}
                        className="block break-all font-display text-lg font-medium text-ink-950 transition-colors hover:text-accent-500"
                      >
                        {email}
                      </a>
                    ))}
                  </dd>
                </Reveal>

                <Reveal delay={260} className="py-6">
                  <dt className="label flex items-center gap-3">
                    <Icon name="pin" className="h-4 w-4 text-accent-500" />
                    Location
                  </dt>
                  <dd className="mt-3 font-display text-lg font-medium text-ink-950">
                    {company.location}
                  </dd>
                </Reveal>
              </dl>
            </div>

            {/* ---------- Form ---------- */}
            <Reveal delay={120}>
              <form onSubmit={handleSubmit} className="space-y-10">
                <div className="grid gap-10 sm:grid-cols-2">
                  <div>
                    <label className="field-label" htmlFor="name">
                      Your name *
                    </label>
                    <input
                      id="name"
                      name="name"
                      type="text"
                      required
                      value={form.name}
                      onChange={update}
                      placeholder="Jane Wanjiru"
                      className="field"
                    />
                  </div>
                  <div>
                    <label className="field-label" htmlFor="email">
                      Email address *
                    </label>
                    <input
                      id="email"
                      name="email"
                      type="email"
                      required
                      value={form.email}
                      onChange={update}
                      placeholder="you@company.co.ke"
                      className="field"
                    />
                  </div>
                  <div>
                    <label className="field-label" htmlFor="phone">
                      Phone
                    </label>
                    <input
                      id="phone"
                      name="phone"
                      type="tel"
                      value={form.phone}
                      onChange={update}
                      placeholder="+254 700 000 000"
                      className="field"
                    />
                  </div>
                  <div>
                    <label className="field-label" htmlFor="subject">
                      Subject *
                    </label>
                    <input
                      id="subject"
                      name="subject"
                      type="text"
                      required
                      value={form.subject}
                      onChange={update}
                      placeholder="Tender enquiry"
                      className="field"
                    />
                  </div>
                </div>

                <div>
                  <label className="field-label" htmlFor="message">
                    How can we help? *
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    rows="6"
                    required
                    value={form.message}
                    onChange={update}
                    placeholder="Tell us about the scope, timelines, and any documents you have."
                    className="field resize-y"
                  />
                </div>

                <div className="flex flex-wrap items-center gap-6">
                  <button type="submit" className="btn btn-solid">
                    Send message
                    <Icon name="arrow" className="h-4 w-4" />
                  </button>
                  {sent && (
                    <span className="inline-flex items-center gap-2 text-sm text-accent-500">
                      <Icon name="check" className="h-4 w-4" />
                      Opening your mail app…
                    </span>
                  )}
                  <span className="text-xs text-ink-400">Fields marked * are required.</span>
                </div>
              </form>
            </Reveal>
          </div>
        </div>
      </section>
    </main>
  )
}

export default Contact
